// src/features/landing/hooks/useEsims.js
"use client";

import { useCallback, useEffect, useState } from "react";
import { fetchEsimOffers } from "@/utils/api/esim";

export function useEsimOffers(_brands, { limit = 20 } = {}) {
  const [esims, setEsims] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [filters, setFilters] = useState({
    offset: 0,
    country: "",
    regions: "",
    subType: "",
    priceMin: undefined,
    priceMax: undefined,
  });

  const load = useCallback(
  async ({ offset, country, regions, subType, priceMin, priceMax, signal }) => {
    const res = await fetchEsimOffers(
      {
        limit,
        offset,
        country: country || undefined,
        regions: regions || undefined,
        subType: subType || undefined,
        priceMin: priceMin ?? undefined,
        priceMax: priceMax ?? undefined,
      },
      { signal }
    );

    setEsims(Array.isArray(res?.list) ? res.list : []);
    setTotal(Number.isFinite(res?.total) ? res.total : (res?.list?.length || 0));
  },
  [limit]
);

useEffect(() => {
  const controller =
    typeof AbortController !== "undefined" ? new AbortController() : null;

  setLoading(true);
  setError(null);

  load({
    offset: filters.offset ?? 0,
    country: filters.country || "",
    regions: filters.regions || "",
    subType: filters.subType || "",
    priceMin: filters.priceMin,
    priceMax: filters.priceMax,
    signal: controller?.signal,
  })
    .catch((e) => {
      // axios cancel might not be AbortError; guard both
      if (e?.name === "AbortError" || e?.code === "ERR_CANCELED") return;

      if (
        e?.code === "AUTH_REQUIRED" ||
        e?.status === 401 ||
        /401/.test(String(e?.message))
      ) {
        setError("Sign in to view eSIM offers.");
        setEsims([]);
        setTotal(0);
        return;
      }

      console.error("❌ useEsimOffers:", e);
      setError(e?.message || "Failed to load eSIMs");
      setEsims([]);
      setTotal(0);
    })
    .finally(() => setLoading(false));

  return () => controller?.abort?.();
}, [
  load,
  filters.offset,
  filters.country,
  filters.regions,
  filters.subType,
  filters.priceMin,
  filters.priceMax,
]);


  const setFilter = (k, v) => setFilters((f) => ({ ...f, [k]: v, offset: 0 }));

  return {
    esims,
    total,
    loading,
    error,
    filters,
    setFilters,
    setFilter,
    limit,
    offset: filters.offset ?? 0,
  };
}
